// Parser mínimo de expresiones para computed columns y footer.
// Soporta:
//   - Literales numéricos: 1, 2.5, 0.19
//   - Paths: row.<key>, data.<key> (resuelven valores del contexto)
//   - Operadores: + - * /  (con precedencia y paréntesis)
//   - sum(<expr>) agrega la expresión sobre todas las filas de `rows`
//
// Deliberadamente NO usa `new Function()` ni `eval` — tokeniza + parsea
// vía Pratt/operator-precedence para evitar XSS si una plantilla viene de
// una fila `pdf_templates` de un tenant.

const isDigit = (c) => c >= '0' && c <= '9';
const isIdStart = (c) => (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_';
const isIdCont = (c) => isIdStart(c) || isDigit(c) || c === '.';

function tokenize(input) {
  const tokens = [];
  let i = 0;
  while (i < input.length) {
    const c = input[i];
    if (c === ' ' || c === '\t' || c === '\n') {
      i++;
      continue;
    }
    if (isDigit(c) || (c === '.' && isDigit(input[i + 1]))) {
      let j = i;
      while (j < input.length && (isDigit(input[j]) || input[j] === '.')) j++;
      tokens.push({ type: 'num', value: Number(input.slice(i, j)) });
      i = j;
      continue;
    }
    if (isIdStart(c)) {
      let j = i;
      while (j < input.length && isIdCont(input[j])) j++;
      tokens.push({ type: 'ident', value: input.slice(i, j) });
      i = j;
      continue;
    }
    if ('+-*/()'.includes(c)) {
      tokens.push({ type: 'op', value: c });
      i++;
      continue;
    }
    if (c === ',') {
      tokens.push({ type: 'comma' });
      i++;
      continue;
    }
    throw new Error(`Caracter inesperado en fórmula: ${c}`);
  }
  return tokens;
}

// Parser descendente recursivo con precedencia estándar.
function parse(tokens) {
  let pos = 0;
  const peek = () => tokens[pos];
  const consume = (type, value) => {
    const t = tokens[pos];
    if (!t || t.type !== type || (value !== undefined && t.value !== value)) {
      throw new Error(`Esperado ${type} ${value ?? ''}, recibido ${JSON.stringify(t)}`);
    }
    pos++;
    return t;
  };

  const parseExpr = () => parseAddSub();

  function parseAddSub() {
    let left = parseMulDiv();
    while (peek() && peek().type === 'op' && (peek().value === '+' || peek().value === '-')) {
      const op = consume('op').value;
      const right = parseMulDiv();
      left = { kind: 'bin', op, left, right };
    }
    return left;
  }

  function parseMulDiv() {
    let left = parseUnary();
    while (peek() && peek().type === 'op' && (peek().value === '*' || peek().value === '/')) {
      const op = consume('op').value;
      const right = parseUnary();
      left = { kind: 'bin', op, left, right };
    }
    return left;
  }

  function parseUnary() {
    if (peek() && peek().type === 'op' && peek().value === '-') {
      consume('op');
      return { kind: 'neg', expr: parsePrimary() };
    }
    return parsePrimary();
  }

  function parsePrimary() {
    const t = peek();
    if (!t) throw new Error('Expresión incompleta');
    if (t.type === 'num') {
      consume('num');
      return { kind: 'num', value: t.value };
    }
    if (t.type === 'op' && t.value === '(') {
      consume('op', '(');
      const expr = parseExpr();
      consume('op', ')');
      return expr;
    }
    if (t.type === 'ident') {
      consume('ident');
      // sum(expr) — única función soportada por ahora.
      if (peek() && peek().type === 'op' && peek().value === '(') {
        consume('op', '(');
        const arg = parseExpr();
        consume('op', ')');
        if (t.value !== 'sum') {
          throw new Error(`Función desconocida: ${t.value}`);
        }
        return { kind: 'call', name: 'sum', arg };
      }
      return { kind: 'path', path: t.value };
    }
    throw new Error(`Token inesperado: ${JSON.stringify(t)}`);
  }

  const ast = parseExpr();
  if (pos !== tokens.length) {
    throw new Error('Tokens sobrantes en fórmula');
  }
  return ast;
}

function resolvePath(path, row, data) {
  const parts = path.split('.');
  if (parts.length < 2) return undefined;
  const [scope, ...rest] = parts;
  let ctx;
  if (scope === 'row') ctx = row;
  else if (scope === 'data') ctx = data;
  else throw new Error(`Scope desconocido: ${scope}`);
  for (const key of rest) {
    if (ctx == null) return undefined;
    ctx = ctx[key];
  }
  return ctx;
}

function evalAst(ast, row, data, rows) {
  switch (ast.kind) {
    case 'num':
      return ast.value;
    case 'neg':
      return -evalAst(ast.expr, row, data, rows);
    case 'bin': {
      const l = Number(evalAst(ast.left, row, data, rows) || 0);
      const r = Number(evalAst(ast.right, row, data, rows) || 0);
      switch (ast.op) {
        case '+':
          return l + r;
        case '-':
          return l - r;
        case '*':
          return l * r;
        case '/':
          return r === 0 ? 0 : l / r;
        default:
          throw new Error(`Op desconocido: ${ast.op}`);
      }
    }
    case 'path': {
      const v = resolvePath(ast.path, row, data);
      return v == null ? 0 : Number(v) || 0;
    }
    case 'call': {
      if (ast.name !== 'sum') throw new Error(`Función no soportada: ${ast.name}`);
      if (!Array.isArray(rows)) return 0;
      return rows.reduce((acc, r) => acc + Number(evalAst(ast.arg, r, data, rows) || 0), 0);
    }
    default:
      throw new Error(`Nodo desconocido: ${ast.kind}`);
  }
}

// Cache simple de parseos (los templates se re-renderean seguido con las mismas fórmulas).
const astCache = new Map();

export function evaluateFormula(formula, { row = {}, data = {}, rows = [] } = {}) {
  if (formula == null || formula === '') return null;
  let ast = astCache.get(formula);
  if (!ast) {
    ast = parse(tokenize(String(formula)));
    astCache.set(formula, ast);
  }
  return evalAst(ast, row, data, rows);
}
